import { trackEventWithOptions } from 'src/utils/analytics';

const generateBridgeId = (): number =>
  Math.floor((crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32) * 100) +
  1;

const BRIDGE_ACTION = {
  sent: 'sent', // Bridge message sent to native app
  completed: 'completed', // Native app opened the browser successfully
  failed: 'failed', // Native app returned an error or response was unparseable
  bridge_unavailable: 'bridge_unavailable', // Bridge not available, fell back to direct navigation
} as const;

const trackBridge = (action: string, url: string) => {
  trackEventWithOptions({
    resourceId: `js-bridge:navigate:${action}`,
    type: 'open',
    resourceName: url,
  });
};

// Fire-and-forget: no onMessage needed since the WebView is being destroyed.
export const closeWebView = (): void => {
  if (window.ringapp?.sendMessage) {
    // Clear any stale handler so a late response doesn't re-trigger prior callbacks.
    window.ringapp.onMessage = () => {};
    window.ringapp.sendMessage(
      JSON.stringify({ id: generateBridgeId(), operation: 'close-window' }),
    );
    trackBridge('close_sent', 'webview');
  }
};

interface NavigateToBrowserOptions {
  sso?: boolean;
  onBeforeNavigate?: () => void;
  onSuccess?: () => void;
}

const appendRefSessId = (targetUrl: string): string => {
  const sessId = window.RWA?.getSessionId?.();
  if (!sessId) return targetUrl;
  try {
    const parsed = new URL(targetUrl);
    parsed.searchParams.set('refSessId', sessId);
    return parsed.toString();
  } catch {
    return targetUrl;
  }
};

export const navigateToBrowser = (
  targetUrl: string,
  options: NavigateToBrowserOptions = {},
): void => {
  const url = appendRefSessId(targetUrl);
  const { sso = true, onBeforeNavigate, onSuccess } = options;
  const message = {
    id: generateBridgeId(),
    operation: 'navigate',
    body: {
      url,
      target: 'browser',
      ...(sso && { sso: { enabled: true } }),
    },
  };

  if (window.ringapp?.sendMessage) {
    // Only fire onBeforeNavigate when the bridge is available — the fallback
    // path navigates the WebView away entirely, so there's no "return" to detect.
    onBeforeNavigate?.();
    window.ringapp.onMessage = (rawMsg: string) => {
      try {
        const msg = JSON.parse(rawMsg);
        if (msg?.result === 'error') {
          trackBridge(BRIDGE_ACTION.failed, url);
          window.location.href = url;
        } else {
          trackBridge(BRIDGE_ACTION.completed, url);
          onSuccess?.();
        }
      } catch {
        trackBridge(BRIDGE_ACTION.failed, url);
        window.location.href = url;
      }
    };
    window.ringapp.sendMessage(JSON.stringify(message));
    trackBridge(BRIDGE_ACTION.sent, url);
  } else {
    trackBridge(BRIDGE_ACTION.bridge_unavailable, url);
    window.location.href = url;
  }
};
